import React, { useState } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useSearch } from "../../../../contexts/SearchContext";
import {
  getAllDepartureDates,
  getAvailableMonthOfDeparture,
  getReturnDatesForDeparture,
  getFallbackLdsDatesForAllDestinations,
  resolveLdsSearchCityFromLocation,
} from "../../../../contexts/SearchContextHelpers";
import DestinationInput from "../SubComponents/DestinationInput/DestinationInput";
import PeopleCounterInput from "../SubComponents/PeopleCounterInput/PeopleCounterInput";
import DatesInput from "../SubComponents/DatesInput/DatesInput";
import { Loader } from "../../../Loader/Loader";
import { OrangeButton } from "../../../buttons/Buttons";
import MobileCategoryPicker from "./MobileCategoryPicker";
import "./MobileSearchWizard.css";
import "../SubComponents/SearchParamsLockButton/SearchParamsLockButton.css";
import "../PackageSearchBars/packagesearchbar.css";
import {
  PACKAGE_QUICK_CATEGORY_ITEMS,
  filterVisiblePackageQuickCategories,
  getPackageCategoryDisplayLabel,
} from "../../../../constants/packageQuickCategoryItems";

const STEPS = 3;

function getRoomsLabel(roomInfo) {
  if (!roomInfo?.rooms?.length) return "—";
  const rooms = roomInfo.rooms;
  const numRooms = rooms.length;
  const totalAdults = rooms.reduce((s, r) => s + (r.adults ?? r.AdultsCount ?? 0), 0);
  const totalKids = rooms.reduce((s, r) => s + (r.kids ?? r.KidsCount ?? 0), 0);
  let text = `${numRooms} חדר${numRooms > 1 ? "ים" : ""}, ${totalAdults} מבוגר${totalAdults !== 1 ? "ים" : ""}`;
  if (totalKids > 0) text += `, ${totalKids} ילד${totalKids !== 1 ? "ים" : ""}`;
  return text;
}

function MobileSearchWizard({ onCloseSearchEditor = null }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);

  const {
    location,
    setLocation,
    roomInfo,
    setRoomInfo,
    categories,
    setCategories,
    calendarDateArrival,
    calendarDateLeave,
    setCalendarDateArrival,
    setCalendarDateLeave,
    ldsDates,
    datesError,
    loadingDates,
    subjects,
    submitSearch,
    isSearchDisabled,
    searchByCategory,
  } = useSearch();

  const locMetaMw = resolveLdsSearchCityFromLocation(location);
  const isAllDestinations = locMetaMw.isAllDestinations;
  const parsedMwMonths =
    Array.isArray(ldsDates) && ldsDates.length > 0
      ? getAvailableMonthOfDeparture(ldsDates).length
      : 0;
  const effectiveLdsDates =
    isAllDestinations &&
    (!Array.isArray(ldsDates) ||
      ldsDates.length === 0 ||
      parsedMwMonths === 0)
      ? getFallbackLdsDatesForAllDestinations()
      : ldsDates;

  const availableCategories = subjects?.FlightsHotel || [];
  const visibleCategoryButtons = filterVisiblePackageQuickCategories(
    availableCategories,
    PACKAGE_QUICK_CATEGORY_ITEMS
  );

  const canStep1Next = !!location;
  const canStep2Next =
    !!calendarDateArrival &&
    !!calendarDateLeave &&
    calendarDateArrival.length > 0 &&
    calendarDateLeave.length > 0;

  const handleBack = () => {
    setStep((s) => Math.max(1, s - 1));
  };

  const handleNext = () => {
    if (step < STEPS) setStep(step + 1);
  };

  const handleSearch = () => {
    submitSearch();
  };

  const handleEditStep = (s) => {
    setStep(s);
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div className="mobile-wizard-step-content">
          <div className="mobile-wizard-field-group">
            <label className="mobile-wizard-label">יעד</label>
            <DestinationInput
              location={location}
              setLocation={setLocation}
              isLdsInput
            />
          </div>
          <div className="mobile-wizard-field-group">
            <label className="mobile-wizard-label">נוסעים וחדרים</label>
            <PeopleCounterInput roomInfo={roomInfo} setRoomInfo={setRoomInfo} />
          </div>
          <div className="mobile-wizard-field-group">
            <label className="mobile-wizard-label">סוג</label>
            <Select
              placeholder="סוג"
              value={categories?.[0]}
              options={availableCategories.map((category) => ({
                value: category,
                label: getPackageCategoryDisplayLabel(category),
              }))}
              onChange={(value) => setCategories([value])}
              className="category-select-field mobile-wizard-select"
              style={{ width: "100%", minHeight: 52, height: 52 }}
              popupMatchSelectWidth={false}
              classNames={{ popup: { root: "category-select-dropdown" } }}
              getPopupContainer={() => document.body}
            />
          </div>
        </div>
      );
    }

    if (step === 2) {
      if (datesError) {
        return (
          <div className="mobile-wizard-step-content mobile-wizard-message">
            {datesError}
          </div>
        );
      }
      if (!location && !effectiveLdsDates?.length) {
        return (
          <div className="mobile-wizard-step-content mobile-wizard-message">
            יש לבחור יעד קודם
          </div>
        );
      }
      if (loadingDates && !isAllDestinations) {
        return (
          <div className="mobile-wizard-step-content mobile-wizard-message">
            <Loader size="small" inline />
          </div>
        );
      }
      return (
        <div className="mobile-wizard-step-content">
          <p className="mobile-wizard-helper">בחר תאריכי יציאה וחזרה</p>
          <div className="mobile-wizard-field-group">
            <DatesInput
              calendarDateArrival={calendarDateArrival}
              calendarDateLeave={calendarDateLeave}
              setCalendarDateArrival={setCalendarDateArrival}
              setCalendarDateLeave={setCalendarDateLeave}
              possibleArrivalDates={getAllDepartureDates(effectiveLdsDates)}
              possibleDepartureDates={getReturnDatesForDeparture(
                effectiveLdsDates,
                calendarDateArrival
              )}
              ldsDates={effectiveLdsDates}
            />
          </div>
        </div>
      );
    }

    if (step === 3) {
      const locationLabel = locMetaMw.isAllDestinations
          ? "כל היעדים"
          : location?.CITYNAME || location?.name || "—";
      const roomsLabel = getRoomsLabel(roomInfo);

      return (
        <div className="mobile-wizard-step-content mobile-wizard-summary">
          <div className="mobile-wizard-summary-row">
            <span className="mobile-wizard-summary-label">יעד</span>
            <span className="mobile-wizard-summary-value">{locationLabel}</span>
            <button
              type="button"
              className="mobile-wizard-edit-link"
              onClick={() => handleEditStep(1)}
            >
              עריכה
            </button>
          </div>
          <div className="mobile-wizard-summary-row">
            <span className="mobile-wizard-summary-label">תאריכים</span>
            <span className="mobile-wizard-summary-value">
              {calendarDateArrival && calendarDateLeave
                ? `${calendarDateArrival} – ${calendarDateLeave}`
                : "—"}
            </span>
            <button
              type="button"
              className="mobile-wizard-edit-link"
              onClick={() => handleEditStep(2)}
            >
              עריכה
            </button>
          </div>
          <div className="mobile-wizard-summary-row">
            <span className="mobile-wizard-summary-label">נוסעים</span>
            <span className="mobile-wizard-summary-value">{roomsLabel}</span>
            <button
              type="button"
              className="mobile-wizard-edit-link"
              onClick={() => handleEditStep(1)}
            >
              עריכה
            </button>
          </div>
          {categories?.[0] && (
            <div className="mobile-wizard-summary-row">
              <span className="mobile-wizard-summary-label">מסנן</span>
              <span className="mobile-wizard-summary-value">
                {visibleCategoryButtons.find((c) => c.key === categories[0])?.label || categories[0]}
              </span>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const isLastStep = step === STEPS;
  const primaryLabel = isLastStep ? t("hotelsearchbar.search") : "הבא";
  const primaryDisabled =
    (step === 1 && !canStep1Next) || (step === 2 && !canStep2Next);
  const showSecondary = step > 1;

  return (
    <div className="mobile-wizard">
      <header className="mobile-wizard-header">
        <div className="mobile-wizard-header-top">
          <span className="mobile-wizard-title">חיפוש חבילה</span>
          <span className="mobile-wizard-step-indicator">
            {step}/{STEPS}
          </span>
        </div>
        {onCloseSearchEditor && step === 1 ? (
          <button
            type="button"
            className="search-engine-inline-close-btn search-engine-inline-close-btn--compact mobile-wizard-editor-close"
            onClick={onCloseSearchEditor}
          >
            סגור
          </button>
        ) : null}
        {step > 1 && (
          <button
            type="button"
            className="mobile-wizard-close"
            onClick={handleBack}
            aria-label="חזרה"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </header>

      <div className="mobile-wizard-quick-search-entry">
        <p className="mobile-wizard-quick-search-label">חיפוש מהיר</p>
        <div className="mobile-wizard-quick-search-scroll">
          {visibleCategoryButtons.map((cat) => {
            const isSelected = categories?.[0] === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                className={`mobile-wizard-quick-btn mobile-wizard-quick-btn-inline ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  if (typeof searchByCategory === "function") {
                    searchByCategory(cat.key, 6);
                  }
                }}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mobile-wizard-body">{renderStepContent()}</div>

      <footer className="mobile-wizard-footer">
        <div className="mobile-wizard-footer-actions">
          {showSecondary && (
            <button
              type="button"
              className="mobile-wizard-btn secondary"
              onClick={handleBack}
            >
              <FontAwesomeIcon icon={faChevronRight} />
              חזרה
            </button>
          )}
          <OrangeButton
            onClick={isLastStep ? handleSearch : handleNext}
            disabled={primaryDisabled}
            className="mobile-wizard-btn primary"
          >
            {primaryLabel}
          </OrangeButton>
        </div>
      </footer>

      <div
        className="mobile-wizard-category-below-cta"
        dir="rtl"
      >
        <MobileCategoryPicker variant="secondary" />
      </div>
    </div>
  );
}

export default MobileSearchWizard;
